import { Checkbox } from '@mantine/core';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';

export interface VirtualCheckboxItem {
  id: number;
  label: string;
}

interface VirtualCheckboxListProps {
  items: VirtualCheckboxItem[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  maxHeight?: number;
  itemHeight?: number;
}

interface CheckboxRowProps {
  id: number;
  label: string;
  checked: boolean;
  onToggle: (id: number) => void;
}

const CheckboxRow: FC<CheckboxRowProps> = memo(function CheckboxRow({
  id,
  label,
  checked,
  onToggle,
}) {
  return (
    <Checkbox
      checked={checked}
      label={label}
      onChange={() => onToggle(id)}
      styles={{ root: { minHeight: 32, alignItems: 'flex-start' } }}
    />
  );
});

const OVERSCAN = 8;

export const VirtualCheckboxList: FC<VirtualCheckboxListProps> = ({
  items,
  selectedIds,
  onToggle,
  maxHeight = 280,
  itemHeight = 36,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const { startIndex, endIndex, offsetY, totalHeight } = useMemo(() => {
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - OVERSCAN);
    const visibleCount = Math.ceil(maxHeight / itemHeight) + OVERSCAN * 2;
    const end = Math.min(items.length, start + visibleCount);

    return {
      startIndex: start,
      endIndex: end,
      offsetY: start * itemHeight,
      totalHeight: items.length * itemHeight,
    };
  }, [itemHeight, items.length, maxHeight, scrollTop]);

  const visibleItems = useMemo(
    () => items.slice(startIndex, endIndex),
    [endIndex, items, startIndex],
  );

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    setScrollTop(element.scrollTop);
  }, []);

  return (
    <div
      onScroll={handleScroll}
      ref={scrollRef}
      style={{ maxHeight, overflow: 'auto' }}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            left: 0,
            position: 'absolute',
            right: 0,
            top: offsetY,
          }}
        >
          {visibleItems.map((item) => (
            <CheckboxRow
              checked={selectedIds.has(item.id)}
              id={item.id}
              key={item.id}
              label={item.label}
              onToggle={onToggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default VirtualCheckboxList;
